/**
 * How long a running turn has been going against its own limit, and when it last recorded
 * progress. Informational only: nothing here changes a limit, stops, retries or reroutes a run.
 *
 * Progress is read only from recorded run_progress rows - the node writes one when the run's
 * redacted output changed, never on a heartbeat. A quiet run is reported as "no progress
 * recorded", never as stalled: the tool may be working without printing anything new.
 * Pure: callers pass the clock in.
 */
import type { RunProgress, RunningTask } from './data';

/**
 * No existing threshold covers run output going quiet (the 30-minute STUCK_AFTER_MS in runs.ts
 * measures total running time), so this is a small fixed one: ten minutes without a newer
 * recorded progress row, while the run is still inside its limit.
 */
export const NO_PROGRESS_WARN_AFTER_MS = 10 * 60_000;

export interface RunTurnView {
  /** Elapsed time since the run started, or null when no start time was recorded. */
  readonly elapsedMs: number | null;
  /** The run's whole allowance from start to its effective limit, or null when either is missing. */
  readonly limitMs: number | null;
  /** When the newest recorded progress row was observed, or null when none was recorded. */
  readonly lastProgressAt: string | null;
  /** How long since that row, or null when there is none. */
  readonly sinceProgressMs: number | null;
  /** Whole minutes without recorded progress, set only while the warning applies. */
  readonly quietMinutes: number | null;
  /** The one-line elapsed/limit/progress text for the Running now list. */
  readonly label: string;
  /** The warning line, or null when it does not apply. */
  readonly warning: string | null;
}

function parse(iso: string | null): number | null {
  if (iso === null) return null;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
}

export function durationLabel(ms: number): string {
  const minutes = Math.floor(Math.max(0, ms) / 60_000);
  if (minutes < 1) return 'under 1 min';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  return minutes % 60 === 0 ? `${hours} h` : `${hours} h ${minutes % 60} min`;
}

/** The newest recorded progress row for one run: highest seq, the order the node wrote them. */
export function latestRecordedProgress(assignmentId: string, rows: readonly RunProgress[]): RunProgress | null {
  let latest: RunProgress | null = null;
  for (const row of rows) {
    if (row.assignment_id !== assignmentId) continue;
    if (latest === null || row.seq > latest.seq) latest = row;
  }
  return latest;
}

export function runTurnView(run: RunningTask, rows: readonly RunProgress[], nowMs: number): RunTurnView {
  const startedMs = parse(run.started_at);
  const deadlineMs = parse(run.deadline_at);
  const elapsedMs = startedMs === null ? null : Math.max(0, nowMs - startedMs);
  const limitMs = startedMs !== null && deadlineMs !== null && deadlineMs > startedMs ? deadlineMs - startedMs : null;
  const latest = latestRecordedProgress(run.assignment_id, rows);
  const progressMs = latest === null ? null : parse(latest.observed_at);
  const lastProgressAt = progressMs === null ? null : latest?.observed_at ?? null;
  const sinceProgressMs = progressMs === null ? null : Math.max(0, nowMs - progressMs);

  const parts: string[] = [];
  if (elapsedMs === null) parts.push('Start time not recorded');
  else if (limitMs !== null) parts.push(`${durationLabel(elapsedMs)} of ${durationLabel(limitMs)} limit`);
  else parts.push(`${durationLabel(elapsedMs)} · limit not recorded`);
  parts.push(sinceProgressMs === null ? 'no progress recorded yet' : `last progress ${durationLabel(sinceProgressMs)} ago`);

  // Quiet time runs from the newest recorded progress, or from the start when none was recorded.
  const quietFromMs = progressMs ?? startedMs;
  const quietMs = quietFromMs === null ? null : Math.max(0, nowMs - quietFromMs);
  const beforeLimit = deadlineMs === null || nowMs < deadlineMs;
  const warn = quietMs !== null && quietMs >= NO_PROGRESS_WARN_AFTER_MS && beforeLimit;
  const quietMinutes = warn ? Math.floor(quietMs / 60_000) : null;
  const warning = quietMinutes === null ? null
    : `No progress recorded for ${quietMinutes} min${progressMs === null ? ' since it started' : ''} - this does not by itself mean it has stalled.`;

  return { elapsedMs, limitMs, lastProgressAt, sinceProgressMs, quietMinutes, label: parts.join(' · '), warning };
}

/** How many trailing lines of a recorded tail are searched for a step. */
const STEP_SEARCH_LINES = 20;
/** The longest step shown on a Running now card. */
const STEP_MAX_CHARS = 160;

/** CSI, OSC and two-character escape sequences, as terminals emit them. */
const TERMINAL_ESCAPES = /\u001b\[[0-?]*[ -/]*[@-~]|\u001b\][^\u0007\u001b]*(?:\u0007|\u001b\\)|\u001b[@-_]/g;
const CONTROL_CHARS = /[\u0000-\u0008\u000b-\u001f\u007f]/g;

/** Whether one line reads as a sentence about the work rather than a piece of terminal output. */
function readsAsStep(line: string): boolean {
  if (line.length < 8) return false;
  // Code, data, prompts, diffs, tables and box drawing are terminal fragments.
  if (/^[{}[\]<>$#|+\-@`\\/=*~^%]/.test(line)) return false;
  if (/[{};]$/.test(line)) return false;
  if (/[\u2500-\u259f]/.test(line)) return false;
  const words = line.split(/\s+/).filter((word) => /\p{L}{2,}/u.test(word));
  if (words.length < 2) return false;
  const visible = line.replace(/\s/g, '');
  const letters = (visible.match(/\p{L}/gu) ?? []).length;
  return letters / visible.length >= 0.6;
}

/**
 * The last recorded progress step: the newest line of the recorded tail that reads as a
 * sentence about the work, or null when there is none. Raw terminal output - code, data,
 * prompts, diffs, escape sequences - is never shown in its place.
 */
export function progressStep(tail: string | null | undefined): string | null {
  if (typeof tail !== 'string' || tail.trim() === '') return null;
  const lines = tail.replace(TERMINAL_ESCAPES, '').replace(/\r\n?/g, '\n').replace(CONTROL_CHARS, '').split('\n');
  for (const raw of lines.slice(-STEP_SEARCH_LINES).reverse()) {
    // A leading list bullet is how many tools mark a step; the step is what follows it.
    const line = raw.replace(/\s+/g, ' ').trim().replace(/^[-*•●⏺·]\s+/u, '');
    if (!readsAsStep(line)) continue;
    return line.length > STEP_MAX_CHARS ? `${line.slice(0, STEP_MAX_CHARS - 1).trimEnd()}…` : line;
  }
  return null;
}
